import type { JSX, ReactNode } from 'react';

import { cn } from '@/lib/cn';

interface SectionProps {
  children: ReactNode;
  id?: string;
  className?: string;
  /** Alternate background used to separate adjacent sections. */
  tone?: 'default' | 'muted';
  labelledBy?: string;
}

export function Section({
  children,
  id,
  className,
  tone = 'default',
  labelledBy,
}: SectionProps): JSX.Element {
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      className={cn(
        'py-16 sm:py-20 lg:py-24',
        tone === 'muted' && 'border-y border-line bg-surface-muted/40',
        className,
      )}
    >
      <div className="shell-container">{children}</div>
    </section>
  );
}

interface SectionHeadingProps {
  /** Rendered above the title. Purely typographic, never the only label. */
  eyebrow?: string;
  title: string;
  description?: string;
  id: string;
  level?: 2 | 3;
  align?: 'start' | 'center';
  className?: string;
  actions?: ReactNode;
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  id,
  level = 2,
  align = 'start',
  className,
  actions,
}: SectionHeadingProps): JSX.Element {
  const Heading = level === 2 ? 'h2' : 'h3';

  return (
    <div
      className={cn(
        'flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between',
        align === 'center' && 'sm:flex-col sm:items-center sm:text-center',
        className,
      )}
    >
      <div className={cn('max-w-2xl', align === 'center' && 'mx-auto')}>
        {eyebrow ? (
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.18em] text-accent">
            {eyebrow}
          </p>
        ) : null}
        <Heading id={id} className="text-2xl sm:text-3xl lg:text-4xl">
          {title}
        </Heading>
        {description ? <p className="mt-4 text-base text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap gap-3">{actions}</div> : null}
    </div>
  );
}
